import { useState, useEffect } from "react";
import { Loader2, Copy, CheckCircle, AlertTriangle, Languages } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { TELEPHONY_CALL_DETAIL_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { fmtDuration } from "./callMeta";

// Speaker A on the left, Speaker B on the right — usually staff and customer,
// but diarisation does not say which is which, so neither is labelled as such.
const SIDE = {
    "Speaker A": { row: "justify-start", bubble: "bg-gray-100 dark:bg-gray-700/60 text-gray-800 dark:text-gray-100", label: "text-blue-600 dark:text-blue-400" },
    "Speaker B": { row: "justify-end",   bubble: "bg-purple-50 dark:bg-purple-900/30 text-gray-800 dark:text-gray-100", label: "text-purple-600 dark:text-purple-400" },
};
const NO_SPEAKER = { row: "justify-start", bubble: "bg-gray-50 dark:bg-gray-800/60 text-gray-800 dark:text-gray-100", label: "text-gray-500" };

/**
 * A call's transcript as a chat-like list.
 *
 * Props:
 *   callId     — telephony call id; the detail endpoint carries the transcript
 *   className  — extra classes on the wrapper
 */
export default function TranscriptView({ callId, className }) {
    const { fetch } = useApiMethods();
    const [call, setCall] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!callId) return undefined;
        let cancelled = false;
        setLoading(true);
        fetch(TELEPHONY_CALL_DETAIL_URL(callId))
            .then((res) => { if (!cancelled) setCall(res?.status !== "fail" && res?.data ? res.data : null); })
            .catch(() => { if (!cancelled) setCall(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [callId]); // eslint-disable-line react-hooks/exhaustive-deps

    const segments = call?.transcript || [];
    const fullText = call?.transcript_text
        || segments.map((s) => (s.speaker ? `${s.speaker}: ${s.text}` : s.text)).join("\n");

    const copy = () => {
        if (!fullText) return;
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    if (loading) {
        return (
            <div className={cn("flex items-center gap-2 py-3 text-xs text-gray-500 dark:text-gray-400", className)}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading transcript…
            </div>
        );
    }
    if (!call) {
        return <p className={cn("py-3 text-xs text-gray-500 dark:text-gray-400", className)}>Could not load the transcript.</p>;
    }

    return (
        <div className={cn("space-y-2", className)}>
            <div className="flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400">
                    {call.transcript_language && (
                        <><Languages className="w-3 h-3" /> {call.transcript_language}</>
                    )}
                </span>
                {fullText && (
                    <button type="button" onClick={copy} title="Copy transcript"
                        className="inline-flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">
                        {copied ? <CheckCircle className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                        {copied ? "Copied" : "Copy"}
                    </button>
                )}
            </div>

            {call.transcript_status === "failed" && call.transcript_error && (
                <div className="flex items-start gap-2 rounded-md p-2 text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span className="break-all">{call.transcript_error}</span>
                </div>
            )}

            {segments.length > 0 ? (
                <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
                    {segments.map((s, i) => {
                        const side = SIDE[s.speaker] || NO_SPEAKER;
                        return (
                            <div key={i} className={cn("flex", side.row)}>
                                <div className={cn("max-w-[85%] rounded-lg px-2.5 py-1.5", side.bubble)}>
                                    <div className="flex items-center gap-2 text-[10px]">
                                        {s.speaker && <span className={cn("font-semibold", side.label)}>{s.speaker}</span>}
                                        {s.start != null && (
                                            <span className="font-mono tabular-nums text-gray-400">{fmtDuration(s.start)}</span>
                                        )}
                                    </div>
                                    <p className="text-xs whitespace-pre-wrap">{s.text}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : call.transcript_text ? (
                <p className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{call.transcript_text}</p>
            ) : call.transcript_status !== "failed" && (
                <p className="text-xs text-gray-500 dark:text-gray-400">No transcript yet.</p>
            )}
        </div>
    );
}
